// <auto-generated>
// Code generated by EDHC (R) Code Generator.
// Changes may cause incorrect behavior and will be lost if the code is
// regenerated.
// Hash: 1086583406
// </auto-generated>
import {
  HttpClient,
  HttpParams,
  HttpHeaders,
  HttpEventType,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

import { Observable, Subject, Subscription } from 'rxjs';
import { SmartListResult, SmartListCriteria } from 'carehub-shared/smartlist';
import { Utils } from 'carehub-shared/utils';

import { ProviderGroupSummary } from './models/providergroup/providergroupsummary';
import { ProviderSummary } from './models/nonentity/providersummary';
import { ContractSummary } from './models/nonentity/contractsummary';
import { Contact } from './models/providergroup/contact';
import { Document } from './models/providergroup/document';
import { BlobResult } from './models/common/blobresult';
import { UploadObject } from '../shared/models/uploadobject';
import { ProviderGroup } from './models/providergroup/providergroup';
import { Clinic } from './models/clinic/clinic';

export interface getFilteredProviderGroups_Filter {
  name: string;
  statusId: number;
  metroAreaId: number;
  stateId: number;
}

export interface getProvidersByProviderGroupId_Filter {
  includeUnassigned: boolean;
  includeSpecifiedOnly: string[];
}

export interface getContractsByProviderGroupId_Filter {
  includeUnassigned: boolean;
  includeSpecifiedOnly: string[];
}

export interface getProviderGroupById_Filter {
  includeChildren: boolean;
}

export interface getActiveProviderGroupClinics_Filter {
  includeUnassigned: boolean;
  includeSpecifiedOnly: string[];
}

@Injectable({
  providedIn: 'root',
})
export class ProviderGroupsApiService {
  constructor(protected http: HttpClient) {}

  getFilteredProviderGroups(
    criteria?: SmartListCriteria,
    queryParams?: Partial<getFilteredProviderGroups_Filter>
  ): Observable<SmartListResult<ProviderGroupSummary>> {
    let url = environment.chiliSauceApiRoot + `/api/ProviderGroups/filtered`;
    if (criteria) {
      url += SmartListCriteria.toQueryParams(criteria);
    }
    const params = Utils.getHttpParams(queryParams);
    return this.http.get<SmartListResult<ProviderGroupSummary>>(url, {
      params: params,
    });
  }

  getProvidersByProviderGroupId(
    providerGroupGuid: string,
    criteria?: SmartListCriteria,
    queryParams?: Partial<getProvidersByProviderGroupId_Filter>
  ): Observable<SmartListResult<ProviderSummary>> {
    let url =
      environment.chiliSauceApiRoot +
      `/api/ProviderGroups/${providerGroupGuid}/providers`;
    if (criteria) {
      url += SmartListCriteria.toQueryParams(criteria);
    }
    const params = Utils.getHttpParams(queryParams);
    return this.http.get<SmartListResult<ProviderSummary>>(url, {
      params: params,
    });
  }

  updateProvidersForProviderGroupId(
    providerGroupGuid: string,
    ids: string[]
  ): Observable<any> {
    const url =
      environment.chiliSauceApiRoot +
      `/api/ProviderGroups/${providerGroupGuid}/providers`;
    return this.http.put<any>(url, ids);
  }

  getContractsByProviderGroupId(
    providerGroupGuid: string,
    criteria?: SmartListCriteria,
    queryParams?: Partial<getContractsByProviderGroupId_Filter>
  ): Observable<SmartListResult<ContractSummary>> {
    let url =
      environment.chiliSauceApiRoot +
      `/api/ProviderGroups/${providerGroupGuid}/contracts`;
    if (criteria) {
      url += SmartListCriteria.toQueryParams(criteria);
    }
    const params = Utils.getHttpParams(queryParams);
    return this.http.get<SmartListResult<ContractSummary>>(url, {
      params: params,
    });
  }

  updateContractsForProviderGroupId(
    providerGroupGuid: string,
    ids: string[]
  ): Observable<any> {
    const url =
      environment.chiliSauceApiRoot +
      `/api/ProviderGroups/${providerGroupGuid}/contracts`;
    return this.http.put<any>(url, ids);
  }

  getContactsByProviderGroupId(
    providerGroupGuid: string
  ): Observable<Contact[]> {
    const url =
      environment.chiliSauceApiRoot +
      `/api/ProviderGroups/${providerGroupGuid}/contacts`;
    return this.http.get<Contact[]>(url);
  }

  updateContactsForProviderGroupId(
    providerGroupGuid: string,
    contacts: Contact[]
  ): Observable<any> {
    const url =
      environment.chiliSauceApiRoot +
      `/api/ProviderGroups/${providerGroupGuid}/contacts`;
    return this.http.put<any>(url, contacts);
  }

  getDocumentsByProviderId(providerId: string): Observable<Document[]> {
    const url =
      environment.chiliSauceApiRoot +
      `/api/ProviderGroups/provider/${providerId}/documents`;
    return this.http.get<Document[]>(url);
  }

  getDocumentsByProviderGroupId(
    providerGroupGuid: string
  ): Observable<Document[]> {
    const url =
      environment.chiliSauceApiRoot +
      `/api/ProviderGroups/${providerGroupGuid}/documents`;
    return this.http.get<Document[]>(url);
  }

  addProviderGroupDocuments(
    providerGroupGuid: string,
    documents: Document[]
  ): Observable<Document[]> {
    const url =
      environment.chiliSauceApiRoot +
      `/api/ProviderGroups/${providerGroupGuid}/documents`;
    return this.http.post<Document[]>(url, documents);
  }

  uploadDocument(upload: UploadObject, metadata: any): Subject<void> {
    const url = environment.chiliSauceApiRoot + `/api/ProviderGroups/upload`;

    if (!upload.file) {
      console.warn('no file specified to upload');
      return;
    }

    const subject = new Subject<void>();
    const fileToUpload = upload.file;
    const formData = new FormData();
    formData.append('file', fileToUpload, fileToUpload.name);

    let headers: HttpHeaders = null;
    if (metadata) {
      headers = new HttpHeaders({
        UploadMetadata: JSON.stringify(metadata),
      });
    }

    this.http
      .post(url, formData, {
        reportProgress: true,
        observe: 'events',
        headers: headers,
      })
      .subscribe((event) => {
        if (event.type === HttpEventType.UploadProgress) {
          upload.progress = Math.round((100 * event.loaded) / event.total);
        } else if (event.type === HttpEventType.Response) {
          const response = <BlobResult>event.body;
          upload.url = response.blobAddress;
          subject.next();
          subject.complete();
        }
      });

    return subject;
  }

  getAllProviderGroups(
    criteria?: SmartListCriteria
  ): Observable<SmartListResult<ProviderGroup>> {
    let url = environment.chiliSauceApiRoot + `/api/ProviderGroups`;
    if (criteria) {
      url += SmartListCriteria.toQueryParams(criteria);
    }
    return this.http.get<SmartListResult<ProviderGroup>>(url);
  }

  addProviderGroup(newProviderGroup: ProviderGroup): Observable<ProviderGroup> {
    const url = environment.chiliSauceApiRoot + `/api/ProviderGroups`;
    return this.http.post<ProviderGroup>(url, newProviderGroup);
  }

  getProviderGroupById(
    providerGroupGuid: string,
    queryParams?: Partial<getProviderGroupById_Filter>
  ): Observable<ProviderGroup> {
    const url =
      environment.chiliSauceApiRoot +
      `/api/ProviderGroups/${providerGroupGuid}`;
    const params = Utils.getHttpParams(queryParams);
    return this.http.get<ProviderGroup>(url, { params: params });
  }

  updateProviderGroup(
    providerGroup: ProviderGroup,
    providerGroupGuid: string
  ): Observable<any> {
    const url =
      environment.chiliSauceApiRoot +
      `/api/ProviderGroups/${providerGroupGuid}`;
    return this.http.put<any>(url, providerGroup);
  }

  deleteProviderGroup(providerGroupGuid: string): Observable<any> {
    const url =
      environment.chiliSauceApiRoot +
      `/api/ProviderGroups/${providerGroupGuid}`;
    return this.http.delete(url);
  }

  createProviderGroupClinic(
    providerGroupGuid: string,
    newClinic: Clinic
  ): Observable<Clinic> {
    const url =
      environment.chiliSauceApiRoot +
      `/api/ProviderGroups/${providerGroupGuid}/clinic`;
    return this.http.post<Clinic>(url, newClinic);
  }

  getActiveProviderGroupClinics(
    providerGroupGuid: string,
    criteria?: SmartListCriteria,
    queryParams?: Partial<getActiveProviderGroupClinics_Filter>
  ): Observable<SmartListResult<Clinic>> {
    let url =
      environment.chiliSauceApiRoot +
      `/api/ProviderGroups/${providerGroupGuid}/clinic`;
    if (criteria) {
      url += SmartListCriteria.toQueryParams(criteria);
    }
    const params = Utils.getHttpParams(queryParams);
    return this.http.get<SmartListResult<Clinic>>(url, { params: params });
  }

  getProviderGroupClinic(
    clinicId: string,
    providerGroupGuid: string
  ): Observable<Clinic> {
    const url =
      environment.chiliSauceApiRoot +
      `/api/ProviderGroups/${providerGroupGuid}/clinics/${clinicId}`;
    return this.http.get<Clinic>(url);
  }

  updateClinicsForProviderGroupId(
    providerGroupGuid: string,
    ids: string[]
  ): Observable<any> {
    const url =
      environment.chiliSauceApiRoot +
      `/api/ProviderGroups/${providerGroupGuid}/clinics`;
    return this.http.put<any>(url, ids);
  }

  deleteProviderGroupClinic(
    providerGroupGuid: string,
    clinicGuid: string
  ): Observable<any> {
    const url =
      environment.chiliSauceApiRoot +
      `/api/ProviderGroups/${providerGroupGuid}/clinics/${clinicGuid}`;
    return this.http.delete(url);
  }
}
